import React, { useState, useEffect, useRef } from 'react';
import { Heart, Home as HomeIcon, Building, MapPin, Plus, Star, Trash2, X, Save } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';

const YachanguFavoritesPage = ({ onSelectDestination }) => {
  const [favorites, setFavorites] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addType, setAddType] = useState(null); // 'home', 'work', or 'custom'
  const [searchInput, setSearchInput] = useState('');
  const [customName, setCustomName] = useState('');
  const searchInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [selectedPlace, setSelectedPlace] = useState(null);

  // Load favorites from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('yachangu_favorites');
    if (saved) {
      try {
        setFavorites(JSON.parse(saved));
      } catch (e) {
        console.error('Error loading favorites:', e);
      }
    }
  }, []);

  // Save favorites to localStorage
  const saveFavorites = (updatedFavorites) => {
    localStorage.setItem('yachangu_favorites', JSON.stringify(updatedFavorites));
    setFavorites(updatedFavorites);
  };

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (showAddModal && searchInputRef.current && window.google && !autocompleteRef.current) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        searchInputRef.current,
        {
          componentRestrictions: { country: 'mw' },
          fields: ['formatted_address', 'geometry', 'name']
        }
      );
      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();
        if (place && place.geometry) {
          setSelectedPlace({
            address: place.formatted_address,
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          });
        }
      });
    }
  }, [showAddModal]);

  const handleAddFavorite = (type) => {
    setAddType(type);
    setShowAddModal(true);
    setSearchInput('');
    setCustomName('');
    setSelectedPlace(null);
    // Clear previous autocomplete
    if (autocompleteRef.current) {
      autocompleteRef.current = null;
    }
  };

  const handleSaveFavorite = () => {
    if (!selectedPlace) return;

    const newFavorite = {
      id: Date.now().toString(),
      name: addType === 'home' ? 'Home' : addType === 'work' ? 'Work' : customName,
      address: selectedPlace.address,
      lat: selectedPlace.lat,
      lng: selectedPlace.lng,
      icon: addType === 'home' ? HomeIcon : addType === 'work' ? Building : MapPin,
      iconName: addType === 'home' ? 'HomeIcon' : addType === 'work' ? 'Building' : 'MapPin',
      iconColor: addType === 'home' ? 'text-blue-600 dark:text-blue-400' : addType === 'work' ? 'text-purple-600 dark:text-purple-400' : 'text-green-600',
      bgColor: addType === 'home' ? 'bg-blue-50 dark:bg-blue-900' : addType === 'work' ? 'bg-purple-50 dark:bg-purple-900' : 'bg-green-50'
    };

    // Check if home/work already exists and replace it
    let updatedFavorites;
    if (addType === 'home' || addType === 'work') {
      updatedFavorites = favorites.filter(f => f.name !== newFavorite.name);
      updatedFavorites.push(newFavorite);
    } else {
      updatedFavorites = [...favorites, newFavorite];
    }

    saveFavorites(updatedFavorites);
    setShowAddModal(false);
  };

  const handleRemoveFavorite = (id) => {
    const updatedFavorites = favorites.filter(fav => fav.id !== id);
    saveFavorites(updatedFavorites);
  };

  const handleFavoriteClick = (favorite) => {
    if (onSelectDestination) {
      onSelectDestination({
        lat: favorite.lat,
        lng: favorite.lng,
        address: favorite.address
      });
    }
  };

  const getIconComponent = (iconName) => {
    switch (iconName) {
      case 'HomeIcon':
        return HomeIcon;
      case 'Building':
        return Building;
      case 'MapPin':
        return MapPin;
      default:
        return MapPin;
    }
  };

  const hasHome = favorites.some(f => f.name === 'Home');
  const hasWork = favorites.some(f => f.name === 'Work');

  return (
    <div className="h-screen bg-green-50 dark:bg-gray-900 overflow-y-auto pb-20 transition-colors">
      {/* Header */}
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-green-100 dark:border-gray-700 z-10 transition-colors">
        <div className="p-6 pb-4">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Heart className="w-8 h-8 text-[#16A34A] mr-3 fill-current" />
            Favorites
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Your saved places for quick access</p>
        </div>
      </div>

      {/* Favorites List */}
      <div className="p-6">
        {/* Add New Favorite Button */}
        <button
          onClick={() => handleAddFavorite('custom')}
          className="w-full bg-white dark:bg-gray-800 border-2 border-dashed border-green-200 dark:border-gray-600 hover:border-green-300 rounded-2xl p-4 mb-4 flex items-center justify-center space-x-3 transition-all active:scale-[0.98]"
        >
          <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
            <Plus className="w-6 h-6 text-green-600 dark:text-green-400" />
          </div>
          <div className="text-left">
            <div className="font-semibold text-gray-900 dark:text-white">Add new favorite</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Save a place you visit often</div>
          </div>
        </button>

        {/* Favorites Grid */}
        <div className="space-y-3">
          {favorites.length === 0 ? (
            <div className="text-center py-12">
              <Heart className="w-16 h-16 text-green-200 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No favorites yet</p>
              <p className="text-sm text-gray-400 mt-2">Add places you visit frequently for quick access</p>
            </div>
          ) : (
            favorites.map((favorite) => {
              const Icon = getIconComponent(favorite.iconName);
              return (
                <div
                  key={favorite.id}
                  className="bg-white dark:bg-gray-800 border border-green-100 dark:border-gray-700 rounded-2xl overflow-hidden hover:border-green-200 transition-all"
                >
                  <button
                    onClick={() => handleFavoriteClick(favorite)}
                    className="w-full p-4 flex items-center space-x-4 active:bg-green-50 transition-colors"
                  >
                    {/* Icon */}
                    <div className={`w-14 h-14 ${favorite.bgColor} rounded-2xl flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-7 h-7 ${favorite.iconColor}`} />
                    </div>

                    {/* Details */}
                    <div className="flex-1 text-left">
                      <div className="font-bold text-gray-900 text-lg">{favorite.name}</div>
                      <div className="text-sm text-gray-500 mt-1">{favorite.address}</div>
                    </div>

                    {/* Star/Featured */}
                    <Star className="w-5 h-5 text-yellow-400 fill-current flex-shrink-0" />
                  </button>

                  {/* Action Buttons */}
                  <div className="border-t border-green-50 px-4 py-3 flex items-center justify-between bg-green-50">
                    <button
                      onClick={() => handleFavoriteClick(favorite)}
                      className="text-[#16A34A] text-sm font-semibold hover:text-green-700 transition-colors"
                    >
                      Set as destination
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFavorite(favorite.id);
                      }}
                      className="text-red-500 text-sm font-semibold hover:text-red-700 transition-colors flex items-center space-x-1"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Remove</span>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Quick Actions */}
        <div className="mt-8">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Quick shortcuts</h2>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => handleAddFavorite('home')}
              disabled={hasHome}
              className={`border rounded-xl p-4 transition-all active:scale-[0.98] ${
                hasHome 
                  ? 'bg-gray-50 border-gray-200 opacity-50 cursor-not-allowed' 
                  : 'bg-white dark:bg-gray-800 border-green-100 dark:border-gray-700 hover:border-green-200'
              }`}
            >
              <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-2">
                <HomeIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="text-sm font-semibold text-gray-900">
                {hasHome ? 'Home Added' : 'Add Home'}
              </div>
            </button>
            <button
              onClick={() => handleAddFavorite('work')}
              disabled={hasWork}
              className={`border rounded-xl p-4 transition-all active:scale-[0.98] ${
                hasWork 
                  ? 'bg-gray-50 border-gray-200 opacity-50 cursor-not-allowed' 
                  : 'bg-white dark:bg-gray-800 border-green-100 dark:border-gray-700 hover:border-green-200'
              }`}
            >
              <div className="w-10 h-10 bg-purple-50 dark:bg-purple-900 rounded-full flex items-center justify-center mx-auto mb-2">
                <Building className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="text-sm font-semibold text-gray-900">
                {hasWork ? 'Work Added' : 'Add Work'}
              </div>
            </button>
          </div>
        </div>

        {/* Info Banner */}
        <div className="mt-6 bg-green-100 dark:bg-green-900/20 rounded-2xl p-4 transition-colors">
          <div className="flex items-start space-x-3">
            <MapPin className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-gray-900 text-sm">Quick tip</h3>
              <p className="text-xs text-gray-600 mt-1">
                Save your frequently visited places to book rides faster. Tap any favorite to set it as your destination instantly.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Favorite Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowAddModal(false)}
          />
          
          {/* Modal */}
          <Card className="relative w-full max-w-md mx-4 mb-4 sm:mb-0">
            {/* Header */}
            <div className="border-b p-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">
                Add {addType === 'home' ? 'Home' : addType === 'work' ? 'Work' : 'Favorite'} Location
              </h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              {addType === 'custom' && (
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">
                    Name this place
                  </label>
                  <input
                    type="text"
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    placeholder="e.g. Gym, Mom's house, Office"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              )}

              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">
                  Search for address
                </label>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Enter address or place name"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              {selectedPlace && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-900">
                    <strong>Selected:</strong> {selectedPlace.address}
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t p-4">
              <Button
                onClick={handleSaveFavorite}
                disabled={!selectedPlace || (addType === 'custom' && !customName.trim())}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 disabled:bg-gray-300"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Location
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default YachanguFavoritesPage;
